import type {ColumnDef} from "@tanstack/react-table"
import {NavLink} from "react-router-dom"
import type {OtkDocumentSearchResult} from "shared"

import {Status} from "@/components/Status"
import {
    backendTypeToDisplay,
    type DocumentRow,
    documentStatusLabels,
    formatSignedAtDate,
    ownerTypeLabels,
    signedSortingFn,
    toDateOnlyISO,
} from "@/lib/documents"
import {formatUaDate} from "@/lib/utils"

/**
 * documents.md's main-page column set, reused by the advanced-search results
 * table with `includePunkt`/`includeStatus` both `true`. Row actions render
 * through the caller's own `DataTable.rowActions` — not part of this column
 * set, per the shared component's dedicated-action-column contract.
 */
export function buildDocumentColumns({
  createdSort,
  includePunkt = false,
  includeSigned = true,
  includeStatus = false,
  punktNameById = {},
  linkTo,
}: {
  /** `Створено` (`createdISO`) is fetched pre-sorted by the backend (`GET /otk/documents`'s own `sort` — see `store/documents.ts`), unlike every other column here which still sorts client-side via `DataTable`'s `getSortedRowModel`. Its header button drives that backend sort instead of tanstack's own toggle. */
  createdSort: { direction: false | "asc" | "desc"; onToggle: () => void }
  includePunkt?: boolean
  /** documents.md shows `Підписано` on every tab, drafts included (as `—`). */
  includeSigned?: boolean
  includeStatus?: boolean
  punktNameById?: Record<string, string>
  /** Builds the `Документ` cell's `NavLink` target for a given row id — `/documents/:id` on the main list, `/documents/search/:id` (current query string preserved) from advanced-search results. */
  linkTo: (id: string) => { pathname: string; search?: string }
}): ColumnDef<DocumentRow>[] {
  const columns: ColumnDef<DocumentRow>[] = [
    {
      id: "document",
      header: "Документ",
      meta: { className: "w-32" },
      cell: ({ row }) => (
        <NavLink
          className="block truncate font-medium hover:underline"
          title={row.original.number}
          to={linkTo(row.original?.id)}
        >
          {row.original.number}
        </NavLink>
      ),
    },
    {
      id: "plate",
      header: "Держ.номер",
      meta: { className: "w-28" },
      cell: ({ row }) => row.original.plate || "—",
    },
    {
      id: "vehicle",
      header: "Марка/Модель",
      meta: { className: "w-40" },
      cell: ({ row }) => (
        <>
          <div className="truncate">{row.original.vehicleBrand || "—"}</div>
          <div className="truncate text-muted-foreground">
            {row.original.vehicleModel}
          </div>
        </>
      ),
    },
    {
      accessorKey: "createdISO",
      header: "Створено",
      meta: { className: "w-28", manualSort: createdSort },
      enableSorting: false,
      cell: ({ row }) => formatUaDate(row.original?.createdISO),
    },
  ]

  if (includeSigned) {
    columns.push({
      accessorKey: "signedISO",
      header: "Підписано",
      meta: { className: "w-28" },
      sortingFn: signedSortingFn,
      // Same `—` glyph the prototype renders at :5183/documents (16.10.2026).
      cell: ({ row }) =>
        row.original.signedISO ? (
          formatSignedAtDate(row.original.signedISO)
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    })
  }

  columns.push({
    accessorKey: "type",
    header: "Тип",
    meta: { className: "w-44" },
  })

  if (includePunkt) {
    columns.push({
      id: "punkt",
      header: "Пункт ОТК",
      meta: { className: "w-40" },
      cell: ({ row }) =>
        punktNameById[row.original?.punktId ?? ""] ?? row.original?.punktId,
    })
  }

  if (includeStatus) {
    columns.push({
      id: "status",
      header: "Статус",
      meta: { className: "w-40" },
      cell: ({ row }) => <Status>{row.original.status === "ВІДПРАВЛЕНО" && row.original.registryAcceptedAt === null
        ? "Очікує ЄДРТЗ"
        : row.original.status}</Status>,
    })
  }

  return columns
}

// `POST /otk/documents/search`'s own result columns — every field
// `otkDocumentsSearchInputSchema` can filter on (OPEN-QUESTIONS.md item 102's
// option (a)), unlike `buildDocumentColumns`'s reduced main-list set which has
// no backing field for `registrationCertificate`/`vehicleType`/`ownerType`/
// `ownerName`/`ownerTaxId` at all. `status` uses the full backend vocabulary
// (`documentStatusLabels`), not the four-tab Ukrainian subset, since a search
// with no status filter narrows to every non-deleted status server-side.
export function buildDocumentSearchColumns({
  punktNameById = {},
  linkTo,
}: {
  punktNameById?: Record<string, string>
  /** Builds the `Документ / ТЗ` cell's `NavLink` target for a given row id — `/documents/search/:id`. */
  linkTo: (id: string) => { pathname: string; search?: string }
}): ColumnDef<OtkDocumentSearchResult>[] {
  return [
    {
      id: "document",
      header: "Документ / ТЗ",
      meta: { className: "w-56" },
      cell: ({ row }) => {
        const doc = row.original
        const plate = doc.plateNumber ?? ""
        const vehicleDisplay = [doc.vehicleBrand, doc.vehicleModel].filter(Boolean).join(" ")
        return (
          <div>
            <NavLink className="font-medium hover:underline" to={linkTo(doc.id)}>
              {doc.number}
            </NavLink>
            {(vehicleDisplay || plate) && (
              <div className="text-muted-foreground">
                {[vehicleDisplay, plate].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: "vin",
      header: "VIN",
      meta: { className: "w-40" },
      cell: ({ row }) => row.original.vin ?? "—",
    },
    {
      id: "registrationCertificate",
      header: "Свідоцтво про реєстрацію",
      meta: { className: "w-44" },
      cell: ({ row }) => row.original.registrationCertificate ?? "—",
    },
    {
      id: "vehicleType",
      header: "Тип ТЗ",
      meta: { className: "w-32" },
      cell: ({ row }) => row.original.vehicleType ?? "—",
    },
    {
      id: "ownerType",
      header: "Тип власника",
      meta: { className: "w-32" },
      cell: ({ row }) =>
        row.original.ownerType ? ownerTypeLabels[row.original.ownerType] : "—",
    },
    {
      id: "ownerName",
      header: "ПІБ / Назва організації",
      meta: { className: "w-48" },
      cell: ({ row }) => row.original.ownerName ?? "—",
    },
    {
      id: "ownerTaxId",
      header: "ІПН / ЄДРПОУ",
      meta: { className: "w-32" },
      cell: ({ row }) => row.original.ownerTaxId ?? "—",
    },
    {
      id: "type",
      header: "Тип",
      meta: { className: "w-44" },
      cell: ({ row }) => backendTypeToDisplay[row.original.type],
    },
    {
      id: "status",
      header: "Статус",
      meta: { className: "w-40" },
      cell: ({ row }) => <Status>{documentStatusLabels[row.original.status]}</Status>,
    },
    {
      id: "createdAt",
      header: "Створено",
      meta: { className: "w-28" },
      cell: ({ row }) => formatUaDate(toDateOnlyISO(row.original.createdAt)),
    },
    {
      accessorKey: "signedAt",
      header: "Підписано",
      meta: { className: "w-28" },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.signedAt ?? null
        const b = rowB.original.signedAt ?? null
        if (a === b) return 0
        if (a === null) return 1
        if (b === null) return -1
        return a.localeCompare(b)
      },
      cell: ({ row }) =>
        row.original.signedAt ? (
          formatSignedAtDate(row.original.signedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
    {
      id: "punkt",
      header: "Пункт ОТК",
      meta: { className: "w-40" },
      cell: ({ row }) =>
        punktNameById[row.original.punktId ?? ""] ?? row.original.punktId ?? "—",
    },
  ]
}
